import React, { useState, useEffect } from "react";
import { Modal, Row, Col, Form, Button, Badge, Spinner } from "react-bootstrap";
import { updateProfilCommercial, addClientTag, removeClientTag, fetchClientTags } from "./DataService";
import { TYPE_CLIENT_OPTIONS, TAILLE_OPTIONS, POTENTIEL_OPTIONS, RELATION_OPTIONS, VOLUME_BADGE } from "./constants";
import ZoneActiviteInput from "./ZoneActiviteInput";

// zone_activite est stocké en base comme une chaîne "Nice, Cannes, Antibes" (colonne texte simple,
// pas de table dédiée) — ces deux fonctions sont le seul endroit qui convertit string <-> liste.
const villesFromString = (s) => (s ? s.split(",").map((v) => v.trim()).filter(Boolean) : []);
const villesToString = (villes) => villes.join(", ");

function ClientDetailModal({ client, show, onClose, onUpdated }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [newTag, setNewTag] = useState("");
  const [addingTag, setAddingTag] = useState(false);
  const [existingTags, setExistingTags] = useState([]);

  useEffect(() => {
    if (show) {
      fetchClientTags().then((d) => setExistingTags(d.tags || [])).catch(() => {});
    }
  }, [show]);

  useEffect(() => {
    if (client) {
      // Si aucune zone n'a jamais été saisie manuellement, on préremplit avec les villes
      // déduites des livraisons passées (suggestion visible, rien n'est sauvegardé tant que
      // l'admin ne clique pas sur Enregistrer).
      const zoneActivite = client.zone_activite || (client.villes_livrees || []).join(", ");

      setForm({
        type_client: client.type_client || "",
        activite_principale: client.activite_principale || "",
        sous_activite: client.sous_activite || "",
        taille_potentiel: client.taille_potentiel || "",
        zone_activite: zoneActivite,
        potentiel_commercial: client.potentiel_commercial || "",
        relation_commerciale: client.relation_commerciale || "",
        produits_principaux: client.produits_principaux || "",
        commentaire_commercial: client.commentaire_commercial || "",
      });
    }
  }, [client]);

  if (!client) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateProfilCommercial(client.id, form);
      onUpdated();
    } catch (e) {
      console.error("Erreur mise à jour profil commercial", e);
    } finally {
      setSaving(false);
    }
  };

  const handleAddTag = async () => {
    const tag = newTag.trim();
    if (!tag) return;
    setAddingTag(true);
    try {
      await addClientTag(client.id, tag);
      setNewTag("");
      onUpdated();
    } catch (e) {
      console.error("Erreur ajout tag", e);
    } finally {
      setAddingTag(false);
    }
  };

  const handleRemoveTag = async (tagId) => {
    try {
      await removeClientTag(client.id, tagId);
      onUpdated();
    } catch (e) {
      console.error("Erreur suppression tag", e);
    }
  };

  const repartition = client.repartition_type_livraison_90j;

  return (
    <Modal show={show} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{client.RaisonSocial || `${client.prenom} ${client.nom}`}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <h6 className="text-muted mb-3">Activité — 90 derniers jours</h6>
        <Row className="mb-4 g-3">
          <Col xs={6} md={3}>
            <div className="small text-muted">Commandes</div>
            <div className="fw-bold fs-5">{client.nb_commandes_90j}</div>
          </Col>
          <Col xs={6} md={3}>
            <div className="small text-muted">Fréquence / semaine</div>
            <div className="fw-bold fs-5">{client.freq_semaine_90j}</div>
          </Col>
          <Col xs={6} md={3}>
            <div className="small text-muted">Panier moyen HT</div>
            <div className="fw-bold fs-5">{client.panier_moyen_90j != null ? `${client.panier_moyen_90j} €` : "—"}</div>
          </Col>
          <Col xs={6} md={3}>
            <div className="small text-muted">Tonnage estimé*</div>
            <div className="fw-bold fs-5">{client.tonnage_estime_90j != null ? `${client.tonnage_estime_90j} t` : "—"}</div>
          </Col>
        </Row>
        {/* Volume mesuré, à confronter au potentiel ressenti saisi plus bas. */}
        <div className="mb-4 p-2 rounded border small">
          <strong>Volume mesuré : </strong>
          {VOLUME_BADGE[client.volume_mesure] ? (
            <>
              <Badge bg={VOLUME_BADGE[client.volume_mesure].bg}>{VOLUME_BADGE[client.volume_mesure].label}</Badge>
              {" "}— {client.freq_semaine_90j} livraisons/semaine · {client.livraisons_mois_90j} / mois ·{" "}
              {Math.round(client.ca_ht_mois_90j || 0).toLocaleString("fr-FR")} € HT/mois
            </>
          ) : (
            <span className="text-muted">aucune livraison sur 90 jours</span>
          )}
          {client.potentiel_commercial === "Fort" && client.volume_mesure === "petit" && (
            <Badge bg="warning" text="dark" className="ms-2" title="Potentiel jugé fort, volume encore petit">🎯 Opportunité</Badge>
          )}
          {client.volume_mesure === "grand" && (
            <Badge bg="success" className="ms-2" title="Gros volume : à fidéliser">⭐ À fidéliser</Badge>
          )}
        </div>
        {repartition && (
          <div className="mb-4 small text-muted">
            Type de livraison : {repartition.flex}% Flex · {repartition.planifiee}% Planifiée · {repartition.urgent}% Urgente
          </div>
        )}
        <div className="mb-4" style={{ fontSize: 11, color: "#9ca3af" }}>
          * Estimé à partir des tranches de poids déclarées à la commande, pas un poids exact.
        </div>

        <h6 className="text-muted mb-2">Tags</h6>
        <div className="mb-3 d-flex flex-wrap gap-2 align-items-center">
          {(client.tags || []).map((t, i) => (
            <Badge
              key={t.id ?? `auto-${i}`}
              bg={t.source === "auto" ? "secondary" : "info"}
              className="d-flex align-items-center gap-1"
              style={{ fontSize: 13, padding: "6px 10px" }}
            >
              {t.tag}
              {t.source === "manual" && (
                <span
                  role="button"
                  onClick={() => handleRemoveTag(t.id)}
                  style={{ cursor: "pointer", marginLeft: 4 }}
                  title="Retirer ce tag"
                >
                  ×
                </span>
              )}
            </Badge>
          ))}
        </div>
        <div className="d-flex gap-2 mb-4">
          <Form.Control
            size="sm"
            list="existing-client-tags"
            placeholder="Ajouter un tag (ex: VIP, urgent, multi-chantiers)"
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAddTag()}
            style={{ maxWidth: 320 }}
          />
          <datalist id="existing-client-tags">
            {existingTags.map((t) => <option key={t} value={t} />)}
          </datalist>
          <Button size="sm" variant="outline-primary" onClick={handleAddTag} disabled={addingTag || !newTag.trim()}>
            {addingTag ? <Spinner size="sm" animation="border" /> : "Ajouter"}
          </Button>
        </div>

        <h6 className="text-muted mb-3">Profil commercial</h6>
        <Row className="g-3">
          <Col md={6}>
            <Form.Label className="small">Type de client</Form.Label>
            <Form.Select size="sm" value={form.type_client} onChange={(e) => setForm({ ...form, type_client: e.target.value })}>
              <option value="">—</option>
              {TYPE_CLIENT_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </Form.Select>
          </Col>
          <Col md={6}>
            <Form.Label className="small">Taille / potentiel</Form.Label>
            <Form.Select size="sm" value={form.taille_potentiel} onChange={(e) => setForm({ ...form, taille_potentiel: e.target.value })}>
              <option value="">—</option>
              {TAILLE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </Form.Select>
          </Col>
          <Col md={6}>
            <Form.Label className="small">Activité principale</Form.Label>
            <Form.Control size="sm" value={form.activite_principale} onChange={(e) => setForm({ ...form, activite_principale: e.target.value })} placeholder="Maçonnerie, plomberie, carrelage..." />
          </Col>
          <Col md={6}>
            <Form.Label className="small">Sous-activité</Form.Label>
            <Form.Control size="sm" value={form.sous_activite} onChange={(e) => setForm({ ...form, sous_activite: e.target.value })} />
          </Col>
          <Col md={12}>
            <Form.Label className="small">
              Zone d'activité
              {!client.zone_activite && (client.villes_livrees || []).length > 0 && (
                <span className="text-muted fst-italic ms-2" style={{ fontSize: 11 }}>
                  suggéré depuis l'historique de livraison
                </span>
              )}
            </Form.Label>
            <ZoneActiviteInput
              villes={villesFromString(form.zone_activite)}
              onChange={(villes) => setForm({ ...form, zone_activite: villesToString(villes) })}
            />
          </Col>
          <Col md={6}>
            <Form.Label className="small">Potentiel commercial</Form.Label>
            <Form.Select size="sm" value={form.potentiel_commercial} onChange={(e) => setForm({ ...form, potentiel_commercial: e.target.value })}>
              <option value="">—</option>
              {POTENTIEL_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </Form.Select>
          </Col>
          <Col md={6}>
            <Form.Label className="small">Relation commerciale</Form.Label>
            <Form.Select size="sm" value={form.relation_commerciale} onChange={(e) => setForm({ ...form, relation_commerciale: e.target.value })}>
              <option value="">—</option>
              {RELATION_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </Form.Select>
          </Col>
          <Col md={12}>
            <Form.Label className="small">Produits principaux</Form.Label>
            <Form.Control
              size="sm"
              as="textarea"
              rows={2}
              value={form.produits_principaux}
              onChange={(e) => setForm({ ...form, produits_principaux: e.target.value })}
              placeholder="Ex: carrelage, colle — saisie libre, à affiner"
            />
          </Col>
          <Col md={12}>
            <Form.Label className="small">Commentaire commercial</Form.Label>
            <Form.Control
              size="sm"
              as="textarea"
              rows={2}
              value={form.commentaire_commercial}
              onChange={(e) => setForm({ ...form, commentaire_commercial: e.target.value })}
            />
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Fermer</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? <Spinner size="sm" animation="border" /> : "Enregistrer le profil commercial"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default ClientDetailModal;
